import { database } from '../firebase';
import { ref, push, onValue, remove, set, get, update } from 'firebase/database';
import { convertParisLocalToUtc } from '../utils/dateHelpers';

/**
 * Service pour gérer les événements spéciaux et les dates exclues
 * Stockés dans Firebase sous 'specialEvents' et 'excludedDates'
 */

/**
 * Écouter tous les événements spéciaux
 */
export function listenToSpecialEvents(callback) {
  try {
    const eventsRef = ref(database, 'specialEvents');
    
    const unsubscribe = onValue(eventsRef, (snapshot) => {
      if (snapshot.exists()) {
        const events = [];
        snapshot.forEach((childSnapshot) => {
          events.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        callback(events);
      } else {
        callback([]);
      }
    }, (error) => {
      console.error('❌ [specialEventsService] Erreur listener:', error);
      callback([]);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur setup listener:', error);
    return () => {};
  }
}

/**
 * Écouter toutes les dates exclues
 */
export function listenToExcludedDates(callback) {
  try {
    const datesRef = ref(database, 'excludedDates');
    
    const unsubscribe = onValue(datesRef, (snapshot) => {
      if (snapshot.exists()) {
        const dates = [];
        snapshot.forEach((childSnapshot) => {
          dates.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });
        callback(dates);
      } else {
        callback([]);
      }
    }, (error) => {
      console.error('❌ [specialEventsService] Erreur listener dates exclues:', error);
      callback([]);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur setup listener dates exclues:', error);
    return () => {};
  }
}

/**
 * Ajouter un événement spécial
 */
export async function addSpecialEvent(eventData) {
  try {
    const { name, year, month, day, hour, minute, color } = eventData;
    
    if (!name || !year || !month || !day || hour === undefined || minute === undefined) {
      throw new Error('Tous les champs sont requis');
    }

    // Calculer le timestamp en convertissant l'heure de Paris en UTC
    // Créer une date locale (comme si c'était en heure de Paris)
    const localDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
    // Convertir cette date locale de Paris en UTC
    const meetingAt = convertParisLocalToUtc(localDate);

    const eventsRef = ref(database, 'specialEvents');
    const newEventRef = await push(eventsRef, {
      name: name.trim(),
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hour: Number(hour),
      minute: Number(minute),
      meetingAt,
      color: color || 'orange', // orange par défaut
      createdAt: Date.now(),
    });

    console.log('✅ [specialEventsService] Événement ajouté:', newEventRef.key);
    return { success: true, id: newEventRef.key };
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur ajout:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Mettre à jour un événement spécial
 */
export async function updateSpecialEvent(eventId, updates) {
  try {
    const eventRef = ref(database, `specialEvents/${eventId}`);
    
    // Utiliser update pour ne modifier que les champs spécifiés
    await update(eventRef, {
      ...updates,
      updatedAt: Date.now(),
    });
    
    console.log('✅ [specialEventsService] Événement mis à jour:', eventId, updates);
    return { success: true };
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur mise à jour:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Supprimer un événement spécial
 */
export async function deleteSpecialEvent(eventId) {
  try {
    const eventRef = ref(database, `specialEvents/${eventId}`);
    await remove(eventRef);
    
    console.log('✅ [specialEventsService] Événement supprimé:', eventId);
    return { success: true };
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur suppression:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Ajouter une date exclue
 */
export async function addExcludedDate(dateData) {
  try {
    const { isoDate, reason } = dateData;
    
    if (!isoDate || !isoDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      throw new Error('Format de date invalide (YYYY-MM-DD requis)');
    }

    const datesRef = ref(database, 'excludedDates');
    const newDateRef = await push(datesRef, {
      isoDate,
      reason: reason || '',
      createdAt: Date.now(),
    });

    console.log('✅ [specialEventsService] Date exclue ajoutée:', newDateRef.key);
    return { success: true, id: newDateRef.key };
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur ajout date exclue:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Supprimer une date exclue
 */
export async function deleteExcludedDate(dateId) {
  try {
    const dateRef = ref(database, `excludedDates/${dateId}`);
    await remove(dateRef);
    
    console.log('✅ [specialEventsService] Date exclue supprimée:', dateId);
    return { success: true };
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur suppression date exclue:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Récupérer tous les événements spéciaux (pour dateHelpers)
 */
export async function getSpecialEvents() {
  try {
    const eventsRef = ref(database, 'specialEvents');
    const snapshot = await get(eventsRef);
    
    if (snapshot.exists()) {
      const events = [];
      snapshot.forEach((childSnapshot) => {
        events.push({
          id: childSnapshot.key,
          ...childSnapshot.val(),
        });
      });
      return events;
    }
    
    return [];
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur récupération:', error);
    return [];
  }
}

/**
 * Récupérer toutes les dates exclues (pour dateHelpers)
 */
export async function getExcludedDates() {
  try {
    const datesRef = ref(database, 'excludedDates');
    const snapshot = await get(datesRef);
    
    if (snapshot.exists()) {
      const dates = [];
      snapshot.forEach((childSnapshot) => {
        dates.push({
          id: childSnapshot.key,
          ...childSnapshot.val(),
        });
      });
      return dates;
    }
    
    return [];
  } catch (error) {
    console.error('❌ [specialEventsService] Erreur récupération dates exclues:', error);
    return [];
  }
}
